import { Check, Clock, Droplet, TriangleAlert } from 'lucide-react';
import { PRAYER_LABEL, fmtTime, type PrayerSettings } from '@shared/prayer';
import { useProgress, useSettings } from '@/lib/store';
import { periodOn } from '@/features/fasting/fastMetrics';
import { LEVEL_LABEL, currentWindow, fmtLeft, statusForLevel, type Level } from './window';

/* Status colours (good / warning / critical) — always paired with an icon and
   a text label, never colour alone. */
export const LEVEL_COLOR: Record<Level, string> = { ontime: 'var(--win-ok)', late: 'var(--win-warn)', ending: 'var(--win-crit)' };

/** The current prayer's time window with a coloured progress bar. Null between sunrise and Dhuhr. */
/** showLeft: the "Xh Ym left" count — the Home hero hides it because it shows the time until the next prayer instead. */
export function PrayerTimer({ settings, now, hour12, compact = false, onLog, showLeft = true }: { settings: PrayerSettings; now: Date; hour12: boolean; compact?: boolean; onLog?: () => void; showLeft?: boolean }) {
  const w = currentWindow(settings, now);
  const mark = useProgress((s) => (w ? s.prayers[w.dayKey]?.marks[w.key] : undefined));
  const prayedAt = useProgress((s) => (w ? s.prayers[w.dayKey]?.prayedAt?.[w.key] : undefined));
  const female = useSettings((s) => s.settings.gender === 'female');
  const excused = useProgress((s) => (female && w ? periodOn(s.periods, w.dayKey, w.dayKey) : null));
  if (!w) return null;
  if (excused) {
    return (
      <div className={`flex items-center gap-2 ${compact ? '' : 'rounded-2xl bg-fill p-3.5'}`} role="status">
        <span className="grid h-6 w-6 place-items-center rounded-full bg-[#e87ba4] text-white"><Droplet size={13} /></span>
        <span className="text-[15px] font-semibold">{PRAYER_LABEL[w.key].en} · Excused today</span>
      </div>
    );
  }
  const prayed = !!mark && mark !== 'missed';
  const span = w.end.getTime() - w.start.getTime();
  // Prayed with "I've prayed": the ball stays where it was at that moment.
  const at = prayed && prayedAt && prayedAt >= w.start.getTime() && prayedAt <= w.end.getTime() ? prayedAt : null;
  const pos = Math.min(100, (((at ?? now.getTime()) - w.start.getTime()) / span) * 100);
  /* Colours fade into each other around each boundary instead of switching
     hard: a blend zone either side of "amber starts" and "red starts". */
  const pct = (d: Date) => ((d.getTime() - w.start.getTime()) / span) * 100;
  const late = pct(w.lateAt), ending = pct(w.endingAt);
  const fade = Math.max(2, Math.min(9, (ending - late) / 3));
  const stops: [number, string][] = [
    [0, 'var(--win-ok)'], [Math.max(0, late - fade), 'var(--win-ok)'], [late + fade, 'var(--win-warn)'],
    [Math.max(late + fade, ending - fade), 'var(--win-warn)'], [Math.min(100, ending + fade), 'var(--win-crit)'], [100, 'var(--win-crit)'],
  ];
  const gradient = `linear-gradient(90deg, ${stops.map(([p, c]) => `${c} ${p.toFixed(2)}%`).join(', ')})`;
  /** the line's colour at a position — what the ball wears */
  const colourAt = (x: number) => {
    for (let i = 0; i < stops.length - 1; i++) {
      const [p0, c0] = stops[i], [p1, c1] = stops[i + 1];
      if (x <= p1) {
        if (c0 === c1 || p1 === p0) return c0;
        const t = Math.round(((x - p0) / (p1 - p0)) * 100);
        return `color-mix(in srgb, ${c1} ${t}%, ${c0})`;
      }
    }
    return stops[stops.length - 1][1];
  };
  /* Prayed: the colour of when it was prayed — the line's colour at that moment,
     or (status chosen from the list) the status: on time green, late amber, jamāʿah blue. */
  const statusColour = mark === 'jamaah' ? 'var(--st-jamaah)' : mark === 'late' || mark === 'qada' ? 'var(--win-warn)' : 'var(--win-ok)';
  const prayedColour = at ? colourAt(pos) : statusColour;
  const color = prayed ? prayedColour : LEVEL_COLOR[w.level];
  const ballColor = prayed ? prayedColour : colourAt(pos);
  const Icon = prayed ? Check : w.level === 'ending' ? TriangleAlert : Clock;

  return (
    <div className={compact ? '' : 'rounded-2xl bg-fill p-3.5'} role="status" aria-live="polite">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-black" style={{ background: color }}><Icon size={14} strokeWidth={2.75} /></span>
          <span className="truncate text-[15px] font-semibold">
            {PRAYER_LABEL[w.key].en} · {prayed ? `Prayed ${mark === 'jamaah' ? 'in jamāʿah' : mark === 'late' ? 'late' : mark === 'qada' ? '(qaḍāʾ)' : 'on time'}` : LEVEL_LABEL[w.level]}
          </span>
        </div>
        {!prayed && showLeft && <span className="shrink-0 text-[15px] font-bold tabular-nums" style={{ color }}>{fmtLeft(w.remainingMs)} left</span>}
      </div>
      {/* The window as a line fading green → amber → red, with a ball riding along it
          at "now". The stretch already passed is dimmed. */}
      <div className="relative mt-3 h-4" aria-hidden>
        <div className="absolute inset-x-0 top-1/2 h-1.5 -translate-y-1/2 overflow-hidden rounded-full">
          <div className="absolute inset-0" style={{ background: gradient }} />
          <div className="absolute inset-y-0 left-0 bg-black/40" style={{ width: `${pos}%` }} />
        </div>
        <div className="absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white transition-[left] duration-1000 ease-linear"
          style={{ left: `${pos}%`, background: ballColor, boxShadow: `0 0 0 4px color-mix(in srgb, ${ballColor} 30%, transparent), 0 0 14px ${ballColor}` }} />
      </div>
      {!compact && (
        <div className="mt-1.5 flex justify-between text-[11px] tabular-nums text-muted">
          <span>{fmtTime(w.start, settings.tz, hour12)}</span>
          <span>{w.lateAt.getTime() < w.endingAt.getTime() ? `amber ${fmtTime(w.lateAt, settings.tz, hour12)} · ` : ''}red {fmtTime(w.endingAt, settings.tz, hour12)}</span>
          <span>ends {fmtTime(w.end, settings.tz, hour12)}</span>
        </div>
      )}
      {!compact && !prayed && <p className="mt-1.5 text-[12px] leading-snug text-muted">{w.hint}</p>}
      {!prayed && onLog && (
        <button type="button" onClick={onLog} className="press mt-3 w-full rounded-xl py-2 text-[14px] font-semibold text-black" style={{ background: color }}>
          I've prayed {PRAYER_LABEL[w.key].en} · {statusForLevel(w.level) === 'ontime' ? 'on time' : 'late'}
        </button>
      )}
    </div>
  );
}
